import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Shield, Plus, X } from "lucide-react";

interface InsuranceInfoFormProps {
  practitionerId: string;
  onUpdate: () => void;
}

interface License {
  type: string;
  number: string;
  state: string;
}

const formSchema = z.object({
  policy: z.string().optional(),
  licenses: z.array(z.object({
    type: z.string().min(1, "License type required"),
    number: z.string().min(1, "License number required"),
    state: z.string().min(1, "State/Province required")
  })),
  insuranceTypes: z.string().optional(),
  offerSuperbill: z.boolean().default(false),
  notes: z.string().optional(),
  isVisible: z.boolean().default(true)
});

export default function InsuranceInfoForm({ practitionerId, onUpdate }: InsuranceInfoFormProps) {
  const { toast } = useToast();
  const { t } = useTranslation('dashboard');
  const queryClient = useQueryClient();

  const { data: insuranceInfo, isLoading } = useQuery({
    queryKey: ['insurance-info', practitionerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('practitioner_insurance_info')
        .select('*')
        .eq('practitioner_id', practitionerId)
        .maybeSingle();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    }
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      policy: insuranceInfo?.insurance_coverage_policy || "",
      licenses: Array.isArray(insuranceInfo?.license_numbers) ? (insuranceInfo.license_numbers as unknown as License[]) : [],
      insuranceTypes: insuranceInfo?.accepted_insurance_types?.join(", ") || "",
      offerSuperbill: insuranceInfo?.offers_superbill || false,
      notes: insuranceInfo?.additional_notes || "",
      isVisible: insuranceInfo?.is_visible !== false
    },
    values: insuranceInfo ? {
      policy: insuranceInfo.insurance_coverage_policy || "",
      licenses: Array.isArray(insuranceInfo.license_numbers) ? (insuranceInfo.license_numbers as unknown as License[]) : [],
      insuranceTypes: insuranceInfo.accepted_insurance_types?.join(", ") || "",
      offerSuperbill: insuranceInfo.offers_superbill || false,
      notes: insuranceInfo.additional_notes || "",
      isVisible: insuranceInfo.is_visible !== false
    } : undefined
  });

  const updateMutation = useMutation({
    mutationFn: async (values: z.infer<typeof formSchema>) => {
      const { data, error } = await supabase
        .from('practitioner_insurance_info')
        .upsert({
          practitioner_id: practitionerId,
          insurance_coverage_policy: values.policy || null,
          license_numbers: values.licenses,
          accepted_insurance_types: values.insuranceTypes 
            ? values.insuranceTypes.split(",").map(t => t.trim()).filter(Boolean)
            : [],
          offers_superbill: values.offerSuperbill,
          additional_notes: values.notes || null,
          is_visible: values.isVisible
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insurance-info', practitionerId] });
      toast({
        title: t('insuranceInfo.updateSuccess'),
        description: t('insuranceInfo.updateSuccess')
      });
      onUpdate();
    },
    onError: (error) => {
      console.error("Error updating insurance info:", error);
      toast({
        title: "Error",
        description: "Failed to update insurance information",
        variant: "destructive"
      });
    }
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    updateMutation.mutate(values);
  };

  const addLicense = () => {
    const currentLicenses = form.getValues('licenses');
    form.setValue('licenses', [...currentLicenses, { type: '', number: '', state: '' }]);
  };

  const removeLicense = (index: number) => {
    const currentLicenses = form.getValues('licenses');
    form.setValue('licenses', currentLicenses.filter((_, i) => i !== index));
  };

  if (isLoading) {
    return <div>{t('loading')}</div>;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          <CardTitle>{t('insuranceInfo.title')}</CardTitle>
        </div>
        <CardDescription>
          {t('insuranceInfo.policyDescription')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="policy"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('insuranceInfo.policy')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('insuranceInfo.policyDescription')}
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <FormLabel>{t('insuranceInfo.licenses')}</FormLabel>
                <Button type="button" variant="outline" size="sm" onClick={addLicense}>
                  <Plus className="h-4 w-4 mr-1" />
                  {t('insuranceInfo.addLicense')}
                </Button>
              </div>
              
              {form.watch('licenses').map((license, index) => (
                <div key={index} className="grid grid-cols-12 gap-3 p-4 border rounded-lg">
                  <div className="col-span-11 grid grid-cols-3 gap-3">
                    <FormField
                      control={form.control}
                      name={`licenses.${index}.type`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm">{t('insuranceInfo.licenseType')}</FormLabel>
                          <FormControl>
                            <Input placeholder="Clinical Psychologist" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name={`licenses.${index}.number`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm">{t('insuranceInfo.licenseNumber')}</FormLabel>
                          <FormControl>
                            <Input placeholder="PSY12345" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name={`licenses.${index}.state`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm">{t('insuranceInfo.licenseState')}</FormLabel>
                          <FormControl>
                            <Input placeholder="CA" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <div className="col-span-1 flex items-end justify-center">
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="icon"
                      onClick={() => removeLicense(index)}
                      className="h-10 w-10"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <FormField
              control={form.control}
              name="insuranceTypes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('insuranceInfo.acceptedInsurance')}</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder={t('insuranceInfo.insuranceDescription')}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {t('insuranceInfo.insuranceDescription')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="offerSuperbill"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>
                      {t('insuranceInfo.superbill')}
                    </FormLabel>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('insuranceInfo.additionalNotes')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('insuranceInfo.additionalNotes')}
                      className="min-h-[80px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isVisible"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>
                      {t('insuranceInfo.visibility')}
                    </FormLabel>
                  </div>
                </FormItem>
              )}
            />

            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? t('basicInformation.saving') : t('insuranceInfo.save')}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}